import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from '@fluentui/react-components'
import { DetailSection, NextPage, PageHero, SubNav } from '../components/PageChrome'
import { Reveal } from '../components/Reveal'
import { Topology } from '../components/Topology'

const searchFlow: [string, string][] = [
  ['User signs in', 'Managed hybrid obtains an OIDC token from Keycloak. Standalone local uses the signed-in Windows context and never contacts a server.'],
  ['Desktop issues the query', 'The local catalog answers immediately over authenticated local IPC. In managed mode the same query also goes to Libris Server over HTTPS.'],
  ['Each catalog retrieves candidates', 'SQLite FTS5 and local vectors search the workstation. PostgreSQL full text and pgvector search shared roots and approved endpoint records.'],
  ['Authorization is revalidated', 'Identity, endpoint policy, source availability and current Windows ACLs filter the candidate set before anything is returned.'],
  ['Desktop merges the answer', 'Results keep their source, page, score and availability, so a person can always see which machine an answer came from.'],
]

const components: [string, string, string][] = [
  ['Libris Desktop', 'Windows workstation', 'Avalonia · .NET · self-contained'],
  ['Libris Agent', 'Windows workstation', 'Background service · local IPC · mTLS'],
  ['Local catalog', 'Windows workstation', 'SQLite · FTS5 · vectors · DPAPI secrets'],
  ['OCR runtime', 'Windows workstation', 'Tesseract 5 · eng + ell'],
  ['Libris Server', 'Office server machine', 'ASP.NET Core · HTTPS · native service'],
  ['Central catalog', 'Office server machine', 'PostgreSQL · pgvector · HNSW'],
  ['Identity provider', 'Office server machine', 'Keycloak · OIDC · roles admin/user'],
  ['Embedding provider', 'External, optional', 'HTTPS API · administrator-enabled'],
]

const faqs = [
  {
    q: 'Is pgvector a separate service to run?',
    a: 'No. pgvector is an extension inside PostgreSQL. It adds a vector column type and similarity indexes to the same central database, so there is no additional server process to operate or back up.',
  },
  {
    q: 'Does every workstation need the Agent?',
    a: 'Every workstation that indexes its own files does — Desktop and Agent install together from one MSI. Shared file-server folders are indexed directly by Libris Server and do not need an agent on the file server.',
  },
  {
    q: 'What does the server keep when a PC is offline?',
    a: 'It keeps the last policy-approved metadata and directory catalog for that endpoint, so search still shows that the document exists. The file itself can only be opened or retrieved while the workstation is online and authorized.',
  },
  {
    q: 'Is Docker required in production?',
    a: 'No. The Docker stack is a development convenience. The intended office server uses native Windows installation with production TLS, PostgreSQL/pgvector, Keycloak, protected secrets and tested backups.',
  },
]

export function Infrastructure() {
  return (
    <main id="main">
      <PageHero
        kicker="Infrastructure"
        title="Local-first by design. Connected when it helps."
        lead="Every application, service, database and external boundary in both deployment modes — including what is optional and what never leaves the machine."
        specs={[
          ['Endpoint', 'Desktop + Agent + SQLite'],
          ['Server', 'ASP.NET Core + PostgreSQL'],
          ['Identity', 'Keycloak · OIDC · PKCE'],
          ['Transport', 'HTTPS · mutual TLS'],
        ]}
      />

      <SubNav
        items={[
          { id: 'standalone', label: 'Standalone local' },
          { id: 'hybrid', label: 'Managed hybrid' },
          { id: 'flow', label: 'How a search travels' },
          { id: 'components', label: 'Component inventory' },
        ]}
      />

      <DetailSection
        id="standalone"
        index="01"
        title="Standalone local"
        lead="One Windows workstation, no server dependency, and no external service unless someone deliberately turns one on."
      >
        <Reveal className="diagram-frame" variant="fade">
          <Topology mode="local" />
        </Reveal>
        <div className="note-row">
          <Reveal as="p" className="body" variant="up">
            <b>Files</b> stay in the local directories the user configured. Libris records where they are, not a copy of
            them.
          </Reveal>
          <Reveal as="p" className="body" variant="up" index={1}>
            <b>Search</b> keeps working with no network at all, answering from the SQLite catalog and local vectors.
          </Reveal>
          <Reveal as="p" className="body" variant="up" index={2}>
            <b>Embeddings</b> are optional. Extracted text is sent to a provider only when a credential is configured and
            policy permits it.
          </Reveal>
        </div>
      </DetailSection>

      <DetailSection
        id="hybrid"
        index="02"
        title="Managed hybrid"
        lead="The complete local capability, plus governed office-wide discovery across enrolled workstations and shared storage."
        tinted
      >
        <Reveal className="diagram-frame" variant="fade">
          <Topology mode="hybrid" />
        </Reveal>
        <div className="note-row">
          <Reveal as="p" className="body" variant="up">
            <b>Endpoints</b> prove machine identity with client certificates and sign every request envelope, in addition
            to the signed-in user's token.
          </Reveal>
          <Reveal as="p" className="body" variant="up" index={1}>
            <b>Only approved fields</b> synchronize. The policy decides which catalog metadata may leave a workstation at
            all.
          </Reveal>
          <Reveal as="p" className="body" variant="up" index={2}>
            <b>Shared roots</b> are indexed by the server directly, including text, OCR, embeddings and effective Windows
            permissions.
          </Reveal>
        </div>
      </DetailSection>

      <DetailSection
        id="flow"
        index="03"
        title="How a search travels"
        lead="The person sees one experience. Each source still enforces its own boundary independently."
      >
        <ol className="flow-steps">
          {searchFlow.map(([title, text], index) => (
            <Reveal as="li" key={title} variant="up" index={index}>
              <b className="mono">{String(index + 1).padStart(2, '0')}</b>
              <strong>{title}</strong>
              <small>{text}</small>
            </Reveal>
          ))}
        </ol>
      </DetailSection>

      <DetailSection
        id="components"
        index="04"
        title="Component inventory"
        lead="What is installed, where it runs, and what it is built on."
        tinted
      >
        <Reveal className="matrix is-three" variant="up">
          <div className="matrix-row is-head mono">
            <span>Component</span>
            <span>Runs on</span>
            <span>Technology</span>
          </div>
          {components.map(([name, host, tech], index) => (
            <div className="matrix-row" key={name} style={{ ['--i' as string]: index }}>
              <span>{name}</span>
              <span className="mono is-dim">{host}</span>
              <span className="mono is-dim">{tech}</span>
            </div>
          ))}
        </Reveal>

        <div className="faq-layout faq-inline">
          <h3 className="display-3">Infrastructure questions</h3>
          <Reveal className="faq" variant="up">
            <Accordion collapsible defaultOpenItems="0">
              {faqs.map((faq, index) => (
                <AccordionItem value={String(index)} key={faq.q}>
                  <AccordionHeader>{faq.q}</AccordionHeader>
                  <AccordionPanel>
                    <p>{faq.a}</p>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </DetailSection>

      <section className="band-tight">
        <div className="shell">
          <NextPage href="/security" label="Next" title="How access is actually decided" />
        </div>
      </section>
    </main>
  )
}
